export type VisitorKind = 'guest' | 'email' | 'message'
export type VisitorAuthMethod = 'none' | 'email-code' | 'message-pin'

export const VISITOR_IDENTITY_VERSION = 2

/** Structural input accepted from the private identity adapter. */
export interface VisitorUser {
  id?: string | null
  msg_id?: string | null
  codekey?: string | null
  namekey?: string | null
  email?: string | null
  pass?: string | null
  pin?: string | null
  sess_id?: string | null
  image?: string | null
  identity_version?: number | string | null
  auth_method?: VisitorAuthMethod | null
  user_email?: string | null
  message_address?: string | null
  email_verified?: boolean | null
}

export type EntryCode = [string, string, string, string, string]

/** Temporary values typed into the entry dialog; never the current visitor. */
export interface EntryDraft {
  messageName: string
  password: string
  email: string
  code: EntryCode
  messageId: string
}

export function emptyEntryDraft(): EntryDraft {
  return {
    messageName: '',
    password: '',
    email: '',
    code: ['', '', '', '', ''],
    messageId: '',
  }
}

/**
 * Application-facing description of the person using MsgDesk.
 *
 * Components use this ordinary model rather than NextAuth users, cookies,
 * reducer action pairs, or provider-specific session shapes. Edit credentials
 * never enter this public model; server-only adapters decode and revalidate
 * them from the encrypted HttpOnly token where compatibility still needs one.
 */
export interface CurrentVisitor {
  identityVersion: number
  kind: VisitorKind
  authMethod: VisitorAuthMethod
  messageId: string
  codeKey: string
  name: string
  /** The person's real account email. Never a codekey@msgdesk.com address. */
  userEmail: string
  emailVerified: boolean
  /** MsgDesk delivery/routing address, separate from the person's email. */
  messageAddress: string
  avatarUrl: string
}

export function guestVisitor(): CurrentVisitor {
  return {
    identityVersion: VISITOR_IDENTITY_VERSION,
    kind: 'guest',
    authMethod: 'none',
    messageId: '',
    codeKey: '',
    name: '',
    userEmail: '',
    emailVerified: false,
    messageAddress: '',
    avatarUrl: '',
  }
}

export function visitorFromUser(user: VisitorUser | null | undefined): CurrentVisitor {
  if (!user) return guestVisitor()

  const identityVersion = Number(user.identity_version ?? 0)
  // Old JWTs overloaded `email` with codekey@msgdesk.com. They cannot be
  // upgraded safely because the token does not say whether the person used an
  // email code or a message PIN. Fail closed and require one fresh sign-in.
  if (identityVersion !== VISITOR_IDENTITY_VERSION) return guestVisitor()

  const messageId = String(user.msg_id ?? '')
  const codeKey = String(user.codekey ?? '')
  const name = String(user.namekey ?? '')
  const messageAddress = String(user.message_address ?? (codeKey ? `${codeKey}@msgdesk.com` : ''))
  const userEmail = String(user.user_email ?? user.email ?? '').trim().toLowerCase()
  const authMethod: VisitorAuthMethod = user.auth_method === 'email-code' || user.auth_method === 'message-pin'
    ? user.auth_method
    : 'none'
  const kind: VisitorKind = messageId || codeKey ? 'message' : userEmail ? 'email' : 'guest'

  return {
    identityVersion,
    kind,
    authMethod,
    messageId,
    codeKey,
    name,
    userEmail,
    emailVerified: user.email_verified === true && Boolean(userEmail),
    messageAddress,
    avatarUrl: String(user.image ?? ''),
  }
}

export function visitorIsPresent(visitor: CurrentVisitor): boolean {
  return visitor.kind !== 'guest'
}

export function visitorLabel(visitor: CurrentVisitor): string {
  if (visitor.kind === 'message') return `@${visitor.name || visitor.codeKey}`
  return visitor.kind === 'email' ? visitor.userEmail : ''
}

export function visitorHref(visitor: CurrentVisitor): string {
  return visitor.kind === 'message' && visitor.codeKey ? `/${visitor.codeKey}` : ''
}
